import { useState, useContext, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  IconButton,
  Avatar,
  useTheme,
  Box,
  InputBase,
  Paper,
  Popper,
  List,
  ListItemButton,
  ListItemText,
  ClickAwayListener,
  Stack,
  Typography,
} from '@mui/material';
import { AnimatePresence, motion } from 'framer-motion';
import { Person, LightMode, DarkMode, Search as SearchIcon } from '@mui/icons-material';
import AuthContext from '../../AuthContext';

const Topbar = () => {
  const navigate = useNavigate();
  const theme = useTheme();
  const auth = useContext(AuthContext);
  const { mode, setMode } = auth;

  const [searchTerm, setSearchTerm] = useState('');
  const [showSuggestions, setShowSuggestions] = useState(false);
  const anchorRef = useRef(null);

  const pages = [
    { name: 'Dashboard', path: '/' },
    { name: 'Disk Erasure', path: '/disk-erasure' },
    { name: 'Activity Log', path: '/activity-log' },
    { name: 'File Explorer', path: '/file-explorer' },
    { name: 'Disk Encryption', path: '/disk-encryption' },
  ];

  const suggestions = pages.filter((page) =>
    page.name.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const handleSearchChange = (e) => {
    const value = e.target.value;
    setSearchTerm(value);
    setShowSuggestions(value.length > 0);
  };

  const handleSelectSuggestion = (path) => {
    setSearchTerm('');
    setShowSuggestions(false);
    navigate(path);
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter' && suggestions.length > 0) {
      handleSelectSuggestion(suggestions[0].path);
    }
  };

  const getUserTypeDisplay = (userType) => {
    if (userType === 'ad') return 'Admin';
    if (userType === 'sa') return 'Super Admin';
    return userType;
  };

  return (
    <Box
      display="flex"
      justifyContent="space-between"
      alignItems="center"
      py={1}
      px={2}
      borderBottom={1}
      borderColor={theme.palette.divider}
      bgcolor={theme.palette.background.paper}
      color={theme.palette.text.primary}
      boxShadow={theme.shadows[1]}
    >
      {/* Search Bar */}
      <Box position="relative" width="40%" ref={anchorRef}>
        <Box position="absolute" left={12} top="50%" sx={{ transform: 'translateY(-50%)' }}>
          <SearchIcon sx={{ color: theme.palette.text.secondary }} />
        </Box>

        <InputBase
          placeholder="Search pages..."
          value={searchTerm}
          onChange={handleSearchChange}
          onKeyPress={handleKeyPress}
          onFocus={() => searchTerm && setShowSuggestions(true)}
          sx={{
            width: '100%',
            pl: 5,
            pr: 2,
            py: 1,
            borderRadius: 50,
            border: 1,
            borderColor: theme.palette.divider,
            bgcolor: theme.palette.background.default,
            color: theme.palette.text.primary,
            '&:hover': { borderColor: theme.palette.primary.main },
            '&:focus': { borderColor: theme.palette.primary.main, outline: 'none' },
          }}
        />

        {/* Suggestions using Popper */}
        <Popper open={showSuggestions && suggestions.length > 0} anchorEl={anchorRef.current} placement="bottom-start" style={{ zIndex: 1300 }}>
          <ClickAwayListener onClickAway={() => setShowSuggestions(false)}>
            <Paper
              sx={{
                mt: 1,
                borderRadius: 2,
                width: anchorRef.current?.offsetWidth || 'auto',
                maxHeight: 300,
                overflowY: 'auto',
              }}
              elevation={3}
            >
              <List disablePadding>
                {suggestions.map((page, idx) => (
                  <ListItemButton key={idx} onMouseDown={() => handleSelectSuggestion(page.path)}>
                    <ListItemText primary={page.name} />
                  </ListItemButton>
                ))}
              </List>
            </Paper>
          </ClickAwayListener>
        </Popper>
      </Box>

      {/* Right Side */}
      <Stack direction="row" alignItems="center" spacing={2}>
        {/* Theme Toggle */}
        <IconButton
          onClick={() => setMode(mode === 'light' ? 'dark' : 'light')}
          sx={{
            bgcolor: theme.palette.mode === 'light' ? 'primary.100' : 'grey.800',
            color: theme.palette.primary.main,
            borderRadius: 2,
            p: 1,
            transition: 'all 0.3s ease',
            '&:hover': {
              bgcolor: theme.palette.mode === 'light' ? 'primary.200' : 'grey.700',
              transform: 'scale(1.1)',
            },
          }}
        >
          <AnimatePresence mode="wait" initial={false}>
            {mode === 'light' ? (
              <motion.div
                key="moon"
                initial={{ rotate: -180, opacity: 0 }}
                animate={{ rotate: 0, opacity: 1 }}
                exit={{ rotate: 180, opacity: 0 }}
                transition={{ duration: 0.3 }}
              >
                <DarkMode sx={{ fontSize: 20 }} />
              </motion.div>
            ) : (
              <motion.div
                key="sun"
                initial={{ rotate: 180, opacity: 0 }}
                animate={{ rotate: 0, opacity: 1 }}
                exit={{ rotate: -180, opacity: 0 }}
                transition={{ duration: 0.3 }}
              >
                <LightMode sx={{ fontSize: 20 }} />
              </motion.div>
            )}
          </AnimatePresence>
        </IconButton>

        {/* User Info */}
        <Stack direction="column" alignItems="flex-start" spacing={0.5} sx={{ display: { xs: 'none', sm: 'flex' }, minWidth: 0 }}>
          <Typography variant="body2" fontWeight={600} noWrap>
            {auth.username}
          </Typography>
          <Box
            component="span"
            px={1}
            py={0.5}
            borderRadius={1}
            bgcolor={theme.palette.primary[100]}
            color={theme.palette.primary.main}
            fontSize={12}
          >
            {getUserTypeDisplay(auth.userType)}
          </Box>
        </Stack>

        {/* Avatar */}
        <IconButton
          sx={{
            bgcolor: theme.palette.mode === 'light' ? 'primary.100' : 'grey.800',
            color: theme.palette.primary.main,
            width: 40,
            height: 40,
            borderRadius: 2,
            '&:hover': {
              bgcolor: theme.palette.mode === 'light' ? 'primary.200' : 'grey.700',
              transform: 'scale(1.05)',
            },
          }}
        >
          <Person sx={{ fontSize: 20 }} />
        </IconButton>
      </Stack>
    </Box>
  );
};

export default Topbar;
